import type { LayerRegistryEntry, StrategyLayerKind } from "@/types/strategy";
import { custom } from "./strategies-custom";
import {
  bearCallSpread,
  bearPutSpread,
  bullCallSpread,
  bullPutSpread,
  cashSecuredPut,
  coveredCall,
  singleCall,
  singlePut,
} from "./strategies-directional";
import { jadeLizard } from "./strategies-income";
import {
  brokenWingButterfly,
  butterfly,
  ironCondor,
  ironFly,
  longCondor,
  shortCondor,
} from "./strategies-multi-leg";
import {
  bearCallLadder,
  bearPutLadder,
  bullCallLadder,
  bullPutLadder,
  ratioBackspread,
  ratioSpread,
} from "./strategies-ratio";
import {
  combo,
  guts,
  strap,
  strip,
  syntheticFuture,
} from "./strategies-synthetic";
import {
  doubleTimeSpread,
  straddle,
  strangle,
  timeSpread,
} from "./strategies-volatility";

const REGISTRY_ENTRIES: LayerRegistryEntry[] = [
  // Single leg
  singleCall,
  singlePut,
  // Covered
  coveredCall,
  cashSecuredPut,
  // Vertical spreads
  bullCallSpread,
  bearPutSpread,
  bullPutSpread,
  bearCallSpread,
  // Multi-leg
  ironFly,
  ironCondor,
  butterfly,
  brokenWingButterfly,
  longCondor,
  shortCondor,
  // Income
  jadeLizard,
  // Ratio / Ladder
  ratioBackspread,
  ratioSpread,
  bullCallLadder,
  bearCallLadder,
  bullPutLadder,
  bearPutLadder,
  // Synthetic
  syntheticFuture,
  combo,
  // Volatility
  straddle,
  strangle,
  strip,
  strap,
  guts,
  // Time-based
  timeSpread,
  doubleTimeSpread,
  // Custom
  custom,
];

export const LAYER_REGISTRY: ReadonlyMap<
  StrategyLayerKind,
  LayerRegistryEntry
> = new Map(REGISTRY_ENTRIES.map((e) => [e.kind, e]));

/** Canonical display order for strategy categories. */
export const STRATEGY_CATEGORY_ORDER = [
  "custom",
  "single",
  "covered",
  "vertical",
  "multi_leg",
  "income",
  "ratio",
  "synthetic",
  "time",
  "volatility",
] as const;

export function getRegistryEntry(kind: StrategyLayerKind): LayerRegistryEntry {
  const entry = LAYER_REGISTRY.get(kind);
  if (!entry) throw new Error(`No registry entry for layer kind: ${kind}`);
  return entry;
}

/** Group registered strategy kinds by category, with optional exclusions. */
export function getGroupedKinds(options?: {
  excludeCustom?: boolean;
  excludeKinds?: readonly StrategyLayerKind[];
  excludeTime?: boolean;
}): Map<string, StrategyLayerKind[]> {
  const groups = new Map<string, StrategyLayerKind[]>();
  for (const [, entry] of LAYER_REGISTRY) {
    if (options?.excludeTime && entry.category === "time") continue;
    if (options?.excludeCustom && entry.kind === "custom") continue;
    if (options?.excludeKinds?.includes(entry.kind)) continue;
    const list = groups.get(entry.category) ?? [];
    list.push(entry.kind);
    groups.set(entry.category, list);
  }
  return groups;
}
